<template>
  <div>
    <audio id="message_ringtone" src="/incoming_message.mp3" muted></audio>
    <audio
      id="conversation_ringtone"
      src="/incoming_conversation.mp3"
      muted
    ></audio>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import mixpanel from 'mixpanel-browser'
import { datadogRum } from '@datadog/browser-rum'
import { getToken, onMessage } from 'firebase/messaging'
import moengage from '@moengage/web-sdk'
import { messaging } from '@/plugins/firebase'
import { KB_BASE_URL, ZOHO_URL } from '@/assets/variables/endpoints'
import { chatGPT, mekariAirene } from '@/common/services'
import { setStorage } from '~/utils/storage'
import {
  initializeMqtt,
  getMqttClient,
  reconnectMqttClient,
  disconnectMqttClient,
  clogMqtt,
} from '~/utils/mqtt'
import {
  initializeMqttSharedWorker,
  disconnectMqttSharedWorker,
} from '~/utils/mqttSharedWorker'
import { eventTypeLists, senderTypeLists } from '@/utils/eventNotification.js'
import { validTokenCookie } from '~/utils/general'
import { generateExpiredAtDate, setSsoSession } from '~/utils/auth'
import { EventBus } from '@/plugins/event-bus'

const DEFAULT_FCM_INTERVAL_CHECK_MS = 10000

export default {
  data() {
    return {
      fcmUnsub: 0,
      fcmIntervalId: 0,
      configPromise: null,
      mqttStatus: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 10,
      isReInitializingMqtt: false,
      isSubscribedMqtt: false,
      mqttAck: {
        buffer: [],
        publishTimer: null,
      },
    }
  },
  computed: {
    ...mapState('template', ['credentials']),
    ...mapState('preferences', ['time_to_refresh_token_sso', 'billing_info']),
    ...mapState('layouts', ['appConfig', 'browser_type']),
    ...mapState('organization', ['permissions']),
    ...mapState('mqtt', ['mqttCredential', 'newUsernameMqtt']),
    enableLoggingMqtt() {
      return this.appConfig?.is_enable_logging_mqtt ?? false
    },
    mqttMixpanelConfig() {
      return this.appConfig?.is_enable_to_store_mixpanel_mqtt ?? {}
    },
  },
  watch: {
    billing_info: {
      handler(newVal) {
        if (newVal.status === 'freeze') {
          this.$store.commit('package_usage/billings/UPDATE_MODAL_FREEZE', true)
        }
      },
      immediate: true,
    },
    time_to_refresh_token_sso(newVal, oldVal) {
      if (newVal > oldVal) {
        this.intervalTimeToRefreshToken()
      }
    },
    credentials(newVal) {
      if (newVal[0]) {
        this.integrateMoengage(newVal[0])
      }
    },
    '$route.name': {
      handler() {
        setTimeout(() => {
          if (window.LOU) {
            this.identifyUserByLouAssist()
            if (this.appConfig?.lou_assist?.length) {
              this.appConfig.lou_assist.forEach((lou) => {
                if (window.LOU) {
                  if (this.$route.fullPath === lou.link) {
                    window.LOU.startTour(lou.experience_name)
                  }
                }
              })
            }
          }
        }, 3000)
      },
      immediate: true,
    },
  },
  created() {
    EventBus.$on('re-initialize-mqtt', async () => {
      // Guard: skip if already re-initializing
      if (this.isReInitializingMqtt) {
        console.info('re-initialize-mqtt already in progress, skipping...')
        return
      }

      if (this.permissions?.mqtt_notification) {
        this.isReInitializingMqtt = true

        try {
          if (this.permissions?.mqtt_multitab_session) {
            // Disconnect SharedWorker MQTT before reinitializing
            await this.disconnectConnectionMqttSharedWorker()
            this.isSubscribedMqtt = false
            setTimeout(() => {
              this.setupMqttSharedWorkerConnection({ isRefreshToken: true })
              this.isReInitializingMqtt = false
            }, 1000)
          } else {
            await this.disconnectMqtt()
            this.isSubscribedMqtt = false
            setTimeout(() => {
              this.setupMqttConnection({ isRefreshToken: true })
              this.isReInitializingMqtt = false
            }, 1000)
          }
        } catch (error) {
          console.error('Error re-initializing MQTT:', error)
          this.isReInitializingMqtt = false
        }
      }
    })
  },
  beforeMount() {
    ;['self_topup', 'company_validation'].forEach((menu) => {
      this.getFeatureFlag(menu)
    })
    this.setBrowserType()
    this.fcmCheck({
      checkLocalStorage: false,
    })
    this.getCredential()
    this.getConfiguration()
    this.intervalFcmCheck()
    if (this.isAdmin()) this.getIntegrationTokenStatus()
    this.getCompanySize()
    this.getChatbotSubscription()
    this.getChatbotPreferences()
    // this.setMixpanelIdentify()
    this.intervalTimeToRefreshToken()
    this.setupQontakOneMixpanel()
    if (process.env.BASE_URL === 'https://chat.qontak.com') this.setRUMUser()
  },
  mounted() {
    this.unregistWorker()
    this.onMessageListener()
    this.initFacebookSDK(document, 'script', 'facebook-jssdk')
    this.integrateLouAssist()
    this.initJimoSDK()
    this.getListOfUserDivision()
    // will turn on when the api from crm is ready
    this.getKBInfo()
    this.checkFlaggingScorecardInbox()
    this.checkFlaggingQualityScoreReport()
    this.setChatbotRollout()
    this.checkExpiredBillingEcommerce()
    this.checkQuotaEcommerce()
    this.getZohoSurvey()
    if (this.permissions?.mqtt_notification) {
      if (this.permissions?.mqtt_multitab_session) {
        this.setupMqttSharedWorkerConnection({ isRefreshToken: false })
      } else {
        this.setupMqttConnection({ isRefreshToken: false })
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.fcmIntervalId)
    localStorage.removeItem('subscribe_token')
    this.clearPublishTimerMqttAck()

    // Flush any remaining buffered events before destroying
    if (this.mqttAck.buffer.length > 0) {
      this.publishBufferedEventsMqttAck()
    }

    // Reset subscription flag
    this.isSubscribedMqtt = false

    EventBus.$off('re-initialize-mqtt')
  },
  methods: {
    /**
     * Store MQTT-related Mixpanel metrics with configurable flags
     * @param {string} eventType - Event type: 'connect', 'reconnect', 'error', 'offline', 'close', 'disconnect'
     * @param {string|null} workerType - Optional: 'SharedWorker' or null for regular MQTT
     * @param {object} additionalData - Optional: Additional data to merge with default metrics
     */
    storeMqttMixpanelMetric(eventType, workerType = null, additionalData = {}) {
      // Check if the event type is enabled in the config
      if (!this.mqttMixpanelConfig[eventType]) {
        return
      }

      // Build event name
      const eventName = workerType
        ? `Qontak MQTT ${workerType} ${eventType}`
        : `Qontak MQTT ${eventType}`

      // Build default metrics object
      const defaultMetrics = {
        Role: this.$auth.user.role,
        Email: this.$auth.user.email,
        'User ID': this.$auth.user.id,
        'Agent name': this.$auth.user.full_name,
        'Company ID': this.getOrganizationForMetric()['Company ID'],
        'Company Name': this.getOrganizationForMetric()['Company Name'],
        'Company Size': this.getOrganizationForMetric()['Company Size'],
      }

      // Merge with additional data
      const metrics = { ...defaultMetrics, ...additionalData }

      // Call the existing storeMixpanelMetric method
      this.storeMixpanelMetric(eventName, metrics)
    },
    async getZohoSurvey() {
      const payload = {
        event_type: 'resolved_room',
      }
      const additional = {
        headers: {
          Authorization: this.$auth.getToken('hub'),
        },
      }
      const {
        data: { data },
      } = await this.$axios.post(`${ZOHO_URL}/trigger`, payload, additional)
      if (
        data &&
        data.link &&
        data.custom_variables.length > 0 &&
        data.variable_values.length > 0
      ) {
        data.variable_values.forEach((item) => {
          window[item.key] = item.value
        })
        const url = data.link.split('/u0026').join('&')
        const [prefixLink] = url.split('?')
        const [, code] = prefixLink.split('zs/')
        this.initZoho(code, data.custom_variables)
        setTimeout(() => {
          this.zsShowPopup()
          // Store metric to mixpanel
          this.storeMixpanelMetric('Received NPS', {
            Role: this.$auth.user.role,
            Email: this.$auth.user.email,
            'Company ID': this.getOrganizationForMetric()['Company ID'],
            'Company Name': this.getOrganizationForMetric()['Company Name'],
          })
        }, 500)
      }
    },
    onMessageIncomingHandler(msg, channel) {
      const type = channel === 'mqtt' ? '🟣' : '🟡'
      clogMqtt(
        `==================${type}-${channel}-${type}==================:`,
        msg,
        this.enableLoggingMqtt
      )

      // channel MQTT is enabled
      if (this.permissions?.mqtt_notification && this.mqttStatus) {
        if (channel === 'mqtt') {
          for (let i = 0; i < eventTypeLists.length; i++) {
            const el = eventTypeLists[i]
            if (el.name === msg?.data?.event_type && el.mqtt) {
              clogMqtt(`🟣 eventName: `, el.name, this.enableLoggingMqtt)
              return this.fcmIncomingHandler(msg)
            }
          }
          for (let i = 0; i < senderTypeLists.length; i++) {
            const el = senderTypeLists[i]
            if (el.name === msg?.data?.sender_type && el.mqtt) {
              clogMqtt(`🟣 senderType: `, el.name, this.enableLoggingMqtt)

              return this.fcmIncomingHandler(msg)
            }
          }
        }

        // handle if the data from fcm but mqtt is enabled
        if (channel === 'fcm') {
          for (let i = 0; i < eventTypeLists.length; i++) {
            const el = eventTypeLists[i]
            if (el.name === msg.data?.event_type && el.fcm) {
              clogMqtt(`🟡 eventName: `, el.name, this.enableLoggingMqtt)
              return this.fcmIncomingHandler(msg)
            }
          }
          for (let i = 0; i < senderTypeLists.length; i++) {
            const el = senderTypeLists[i]
            if (el.name === msg?.data?.sender_type && el.fcm) {
              clogMqtt(`🟡 senderType: `, el.name, this.enableLoggingMqtt)
              return this.fcmIncomingHandler(msg)
            }
          }
        }
      } else {
        // when mqttStatus is false switch to FCM
        clogMqtt(
          `🟡 mqttStatus is false, switch to FCM: `,
          msg,
          this.enableLoggingMqtt
        )
        return this.fcmIncomingHandler(msg)
      }
    },
    async setupMqttConnection(refreshToken) {
      try {
        // 1. First get credentials
        await this.$store.dispatch('mqtt/getMqttCredentials', {
          ...refreshToken,
        })
        // 2. Then initialize MQTT connection
        await this.initMqtt()
      } catch (error) {
        clogMqtt(
          `Failed to setup MQTT connection: `,
          error,
          this.enableLoggingMqtt
        )
        this.handleMqttSetupError(error)
      }
    },
    async setupMqttSharedWorkerConnection(refreshToken) {
      try {
        // 1. First get credentials
        await this.$store.dispatch('mqtt/getMqttCredentials', {
          ...refreshToken,
        })

        // 2. Prepare MQTT options
        await this.initMqttSharedWorker()
      } catch (error) {
        clogMqtt(
          `Failed to setup SharedWorker MQTT connection:`,
          error,
          this.enableLoggingMqtt
        )
        this.handleMqttSharedWorkerSetupError(error)
      }
    },
    async handleMqttSharedWorkerSetupError(error) {
      clogMqtt(
        `Error setting up SharedWorker MQTT connection:`,
        error,
        this.enableLoggingMqtt
      )
      await this.updateMqttCredWhenRefreshToken()
      this.mqttStatus = false
      this.$store.commit('mqtt/SET_MQTT_SW_CONNECTED', false)

      if (!this.mqttStatus) {
        console.error(
          'Error setting up SharedWorker MQTT connection. Switch to FCM / enabled FCM notification'
        )
      }
    },
    async handleMqttSetupError(error) {
      clogMqtt(
        `Error setting up MQTT connection: `,
        error,
        this.enableLoggingMqtt
      )
      await this.updateMqttCredWhenRefreshToken()
      this.mqttStatus = false
      if (!this.mqttStatus) {
        console.error('Error setting up MQTT connection. Switch to FCM')
      }
    },
    bufferPush(event) {
      // Validate event has required fields
      if (!event || !event.event_type) {
        clogMqtt(
          'bufferPush: Invalid event - missing event_type',
          event,
          this.enableLoggingMqtt
        )
        return
      }

      const mqttLogConfig = this.permissions?.mqtt_log
      if (!mqttLogConfig) return

      // Check if this event type should be logged
      const shouldLogEvent = mqttLogConfig[event.event_type]
      if (!shouldLogEvent) return

      const triggeredAt = event.triggered_at
        ? new Date(event.triggered_at).getTime()
        : 0
      const publishedAt = event.published_at
        ? new Date(event.published_at).getTime()
        : 0
      const receivedAt = Date.now()

      // Ensure latency values are not negative
      const deliveryLatencyMs = Math.max(0, receivedAt - publishedAt)
      const e2eLatencyMs = Math.max(0, receivedAt - triggeredAt)

      const appVersion = this.$config.appversion
      const eventLog = {
        event_id: event.event_id,
        event_type: event.event_type,
        organization_id: event.organization_id,
        user_id: this.$auth.user.id,
        triggered_at: new Date(triggeredAt).toISOString(),
        published_at: new Date(publishedAt).toISOString(),
        device_id: this.getDeviceId(),
        platform: 'web',
        delivery_latency_ms: deliveryLatencyMs,
        e2e_latency_ms: e2eLatencyMs,
        received_at: new Date(receivedAt).toISOString(),
        app_version: appVersion === 'bitbucket' ? 'localhost' : appVersion,
        log_type: 'received',
        provider: 'mqtt',
      }

      this.mqttAck.buffer.push(eventLog)
      clogMqtt(`Buffered event: `, eventLog, this.enableLoggingMqtt)
      clogMqtt(
        `Pushed to buffer event: `,
        event.event_type,
        this.enableLoggingMqtt
      )

      this.checkAndSendBufferedMessagesMqttAck()
    },
    initMqttSharedWorker() {
      const token = validTokenCookie()
        ? validTokenCookie().replace(/^bearer\s+/i, '')
        : ''

      let mqttOptions = {
        port: 443,
        connectTimeout: 30 * 1000, // Timeout after 30 seconds
        reconnectPeriod: 5000,
        keepalive: 60, // keep alive 60 seconds
        clientId: token,
        username: this.$auth.user.id,
        password: token,
        protocolVersion: parseInt(process.env.MQTT_PROTOCOL_VERSION, 10) || 5,
      }

      if (this.permissions.new_authn_authz_mqtt) {
        mqttOptions = {
          ...mqttOptions,
          clientId: `${this.$auth.user.id}-${token}`,
          username: localStorage.getItem('mqtt-credentials')
            ? JSON.parse(localStorage.getItem('mqtt-credentials') || '')
                .username
            : '',
          password: '',
        }
      }

      // 3. Initialize SharedWorker MQTT connection
      const client = initializeMqttSharedWorker(
        process.env.MQTT_BROKER_URL,
        mqttOptions,
        this.enableLoggingMqtt
      )

      // 4. Setup status change handler
      client.onStatusChange((status) => {
        clogMqtt(
          'SharedWorker MQTT status changed:',
          status,
          this.enableLoggingMqtt
        )

        if (status.connected) {
          this.mqttStatus = true
          this.$store.commit('mqtt/SET_MQTT_SW_CONNECTED', true)
          this.reconnectAttempts = 0
          this.startPublishTimerMqttAck()

          // Track connected in Mixpanel
          this.storeMqttMixpanelMetric('connect', 'SharedWorker')

          // Subscribe to topic after connection (only if not already subscribed)
          if (this.isSubscribedMqtt) {
            clogMqtt(
              'Already subscribed to MQTT topic, skipping...',
              null,
              this.enableLoggingMqtt
            )
            return
          }

          const orgId = this.$auth.user.organization_id
          const userId = this.$auth.user.id

          if (orgId && userId) {
            const topic = `org/${orgId}/user/${userId}`
            client.subscribe(topic, (mqttEvent, topic, message) => {
              clogMqtt(
                `Message received on topic ${topic}:`,
                mqttEvent,
                this.enableLoggingMqtt
              )
              this.onMessageIncomingHandler(mqttEvent, 'mqtt')
              this.bufferPush(mqttEvent)
              this.storeMqttMixpanelMetric('message-event', 'SharedWorker')
            })

            this.isSubscribedMqtt = true
            clogMqtt(
              `Subscribed to topic: ${topic}`,
              null,
              this.enableLoggingMqtt
            )
          } else {
            clogMqtt(
              'MQTT_ORG_ID and MQTT_USERNAME are required',
              null,
              this.enableLoggingMqtt
            )
          }
        } else {
          this.mqttStatus = false
          this.$store.commit('mqtt/SET_MQTT_SW_CONNECTED', false)
          if (status.reason) {
            clogMqtt(
              `SharedWorker MQTT disconnected: ${status.reason}`,
              null,
              this.enableLoggingMqtt
            )

            // Track disconnected in Mixpanel
            this.storeMqttMixpanelMetric('disconnect', 'SharedWorker', {
              Reason: status.reason,
            })
          }
        }
      })

      // 5. Setup error handler
      client.onEvent('error', (payload) => {
        clogMqtt(
          `SharedWorker MQTT error [${payload.context}]:`,
          payload.error,
          this.enableLoggingMqtt
        )

        // Track error in Mixpanel
        this.storeMqttMixpanelMetric('error', 'SharedWorker', {
          Context: payload.context,
          Error: payload.error,
        })

        // Re-initialize connection on auth failure (stale/invalid credentials)
        if (
          payload.error?.includes('Bad User Name or Password') &&
          !this.isReInitializingMqtt
        ) {
          clogMqtt(
            'Auth error detected (SharedWorker) — triggering re-initialize-mqtt',
            null,
            this.enableLoggingMqtt
          )
          EventBus.$emit('re-initialize-mqtt')
        }
      })

      // 6. Setup subscription event handler
      client.onEvent('subscribed', (payload) => {
        clogMqtt(
          `Subscribed to topic via SharedWorker:`,
          payload.topic,
          this.enableLoggingMqtt
        )
        this.mqttStatus = true
        this.reconnectAttempts = 0
      })

      // 7. Log tabs count
      client.onEvent('tabsCount', (payload) => {
        clogMqtt(`Connected tabs count:`, payload.count, this.enableLoggingMqtt)
      })

      // 8. Handle cross-tab logout relay — fires when another tab triggers logout
      client.onLogout(() => {
        clogMqtt(
          'Cross-tab LOGOUT received — signing out this tab',
          null,
          this.enableLoggingMqtt
        )
        // SwitchAccount.vue listens to this event via EventBus.$on('user-sign-out', this.doSignOut)
        EventBus.$emit('user-sign-out')
      })

      clogMqtt(
        'SharedWorker MQTT initialized successfully',
        null,
        this.enableLoggingMqtt
      )
    },
    initMqtt() {
      const token = validTokenCookie()
        ? validTokenCookie().replace(/^bearer\s+/i, '')
        : ''
      let mqttOptions = {
        port: 443,
        connectTimeout: 30 * 1000, // Ticmeout after 30 seconds
        reconnectPeriod: 5000,
        keepalive: 60, // keep alive 60 seconds
        clientId: token,
        username: this.$auth.user.id,
        password: token,
        protocolVersion: parseInt(process.env.MQTT_PROTOCOL_VERSION, 10) || 5,
      }
      if (this.permissions.new_authn_authz_mqtt) {
        mqttOptions = {
          ...mqttOptions,
          clientId: `${this.$auth.user.id}-${token}`,
          username: localStorage.getItem('mqtt-credentials')
            ? JSON.parse(localStorage.getItem('mqtt-credentials') || '')
                .username
            : '',
          password: '',
        }
      }
      initializeMqtt(
        process.env.MQTT_BROKER_URL,
        mqttOptions,
        this.enableLoggingMqtt
      )

      // Access the MQTT client
      const mqttClient = getMqttClient()
      const orgId = this.$auth.user.organization_id
      const userId = this.$auth.user.id

      mqttClient.on('connect', () => {
        clogMqtt('on MQTT connected', null, this.enableLoggingMqtt)
        this.mqttStatus = true
        this.reconnectAttempts = 0
        this.startPublishTimerMqttAck()
        this.storeMqttMixpanelMetric('connect')
      })

      if (!orgId || !userId) {
        clogMqtt(
          'MQTT_ORG_ID and MQTT_USERNAME are required',
          null,
          this.enableLoggingMqtt
        )
        return
      }

      // Subscribe to a topic
      const topic = `org/${orgId}/user/${userId}`
      this.subscribeToTopicMqtt(topic)

      // Listen for messages
      mqttClient.on('message', (topic, message) => {
        const textDecoder = new TextDecoder()
        const jsonString = textDecoder.decode(message)
        let jsonObject = ''
        try {
          jsonObject = JSON.parse(jsonString)
        } catch (error) {
          jsonObject = jsonString
        }
        this.onMessageIncomingHandler(jsonObject, 'mqtt')
        this.bufferPush(jsonObject)
      })

      mqttClient.on('error', (err) => {
        clogMqtt(`on MQTT error: `, err, this.enableLoggingMqtt)

        this.storeMqttMixpanelMetric('error', null, {
          Error: err?.toString() || 'Unknown error',
        })

        // Re-initialize connection on auth failure (stale/invalid credentials)
        if (
          err?.toString()?.includes('Bad User Name or Password') &&
          !this.isReInitializingMqtt
        ) {
          clogMqtt(
            'Auth error detected (MQTT) — triggering re-initialize-mqtt',
            null,
            this.enableLoggingMqtt
          )
          EventBus.$emit('re-initialize-mqtt')
        }
      })

      mqttClient.on('close', () => {
        clogMqtt(`on MQTT connection closed`, null, this.enableLoggingMqtt)
        this.mqttStatus = false
        // Note: MQTT library handles reconnection automatically via reconnectPeriod option
        // Manual attemptReconnectMqtt() removed to prevent double reconnection attempts
        this.storeMqttMixpanelMetric('close')
      })

      mqttClient.on('offline', (offline) => {
        clogMqtt(`on MQTT offline: `, offline, this.enableLoggingMqtt)
        this.mqttStatus = false
        this.storeMqttMixpanelMetric('offline')
      })

      mqttClient.on('disconnect', (disconnect) => {
        clogMqtt(`on MQTT disconnect: `, disconnect, this.enableLoggingMqtt)
        this.mqttStatus = false
        this.storeMqttMixpanelMetric('disconnect')
      })

      mqttClient.on('reconnect', (reconnect) => {
        clogMqtt(`on MQTT reconnect: `, reconnect, this.enableLoggingMqtt)
        this.storeMqttMixpanelMetric('reconnect')
      })
    },
    subscribeToTopicMqtt(topic) {
      // Guard: skip if already subscribed
      if (this.isSubscribedMqtt) {
        clogMqtt(
          'Already subscribed to MQTT topic, skipping...',
          null,
          this.enableLoggingMqtt
        )
        return
      }

      const mqttClient = getMqttClient()
      mqttClient.subscribe(topic, (err) => {
        if (!err) {
          clogMqtt(`Subscribed to topic: `, topic, this.enableLoggingMqtt)
          this.mqttStatus = true
          this.reconnectAttempts = 0
          this.isSubscribedMqtt = true
        } else {
          clogMqtt(
            `Error subscribing to topic ${topic}: `,
            err,
            this.enableLoggingMqtt
          )
        }
      })
    },
    attemptReconnectMqtt() {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        clogMqtt(
          'Max reconnect attempts reached. Stopping further retries.',
          null,
          this.enableLoggingMqtt
        )
        return
      }

      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000) // Exponential backoff, capped at 30 seconds
      this.reconnectAttempts++
      clogMqtt(
        `Reconnecting in ${delay / 1000} seconds (Attempt ${
          this.reconnectAttempts
        }/${this.maxReconnectAttempts})`,
        null,
        this.enableLoggingMqtt
      )

      setTimeout(() => {
        if (!this.mqttStatus) {
          reconnectMqttClient() // Trigger reconnection
        }
      }, delay)
    },
    setBrowserType() {
      this.$store.commit('layouts/BROWSER_TYPE_UPDATE')
    },
    async intervalFcmCheck() {
      await this.configPromise.catch(() => {})
      const FCM_INTERVAL_MS =
        this.appConfig?.interval_refresh_fcm || DEFAULT_FCM_INTERVAL_CHECK_MS

      this.fcmIntervalId = setInterval(() => {
        this.fcmCheck({
          checkLocalStorage: true,
        })
      }, FCM_INTERVAL_MS)
    },
    fcmCheck(options = {}) {
      if (
        this.browser_type !== 'Safari' &&
        !this.$route.path.startsWith('/survey')
      ) {
        this.$store
          .dispatch('fcm/getBrowserNotificationPermission', {})
          .then((allowance) => {
            if (allowance) {
              this.getFcmRegistrationToken({
                browser: this.browser_type,
                checkLocalStorage: options.checkLocalStorage,
              })
              // this.startTokenRefreshListener()
              this.startOnMessageListener()
            }
          })
      }
    },
    async getFcmRegistrationToken({ checkLocalStorage }) {
      await getToken(messaging, {
        vapidKey: process.env.HUB_FCM_VAPID_KEY,
      })
        .then((currentToken) => {
          if (currentToken) {
            /**
             * Only PUT token when init/refresh page, or when token changes during interval
             */
            if (
              checkLocalStorage &&
              currentToken === localStorage.getItem('subscribe_token')
            ) {
              return
            }
            this.$store.dispatch('fcm/getFcmRegistrationToken', {
              token: currentToken,
            })
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('An error occurred while retrieving token. ', err)
        })
      // this.$store.dispatch('fcm/getFcmRegistrationToken', {})
    },
    startTokenRefreshListener() {
      this.$store.dispatch('fcm/onTokenRefreshListener')
    },
    startOnMessageListener() {
      // this.$fire.messaging.onMessage((payload) => {
      //   this.fcmIncomingHandler(payload)
      // })

      /**
       * Actually calling onMessage multiple times is still safe now, but let's prevent
       */
      if (this.fcmUnsub) {
        return
      }
      this.fcmUnsub = onMessage(messaging, (payload) => {
        if (payload.data === undefined || !payload.data) {
          return
        }
        if (typeof payload.data !== 'object') {
          payload.data = JSON.parse(payload.data)
        }
        this.onMessageIncomingHandler(payload, 'fcm')
        // this.fcmIncomingHandler(payload)
      })
    },
    getIntegrationTokenStatus() {
      this.$store.dispatch('channels/facebook/getConnection', true)
    },
    getCredential() {
      this.$store.dispatch('template/getCredential')
    },
    getConfiguration() {
      this.configPromise = this.$store.dispatch('layouts/getConfiguration')
    },
    unregistWorker() {
      /**
       * @TODO this causes sw deleted on page reload/refresh
       */
      // window.addEventListener('beforeunload', async (event) => {
      //   if (window.navigator && navigator.serviceWorker) {
      //     await navigator.serviceWorker.getRegistrations().then(function(registrations) {
      //       for (const registration of registrations) {
      //         registration.unregister()
      //       }
      //     })
      //   }
      //   return ''
      // })
    },
    onMessageListener() {
      navigator.serviceWorker.addEventListener(
        'message',
        (event) => {
          if (!event.data.mode) {
            if (typeof event.data !== 'object') {
              event.data = JSON.parse(event.data)
            }
            // this.fcmIncomingHandler(event)
            this.onMessageIncomingHandler(event, 'fcm')
          }
        },
        false
      )
    },
    initFacebookSDK(d, s, id) {
      let js = null
      const fjs = d.getElementsByTagName(s)[0]
      if (d.getElementById(id)) {
        return
      }
      js = d.createElement(s)
      js.id = id
      js.src = 'https://connect.facebook.net/en_US/sdk.js'
      fjs.parentNode.insertBefore(js, fjs)
    },
    getFeatureFlag(menu) {
      this.$store.dispatch('preferences/getFeatureFlag', menu)
    },
    setMixpanelIdentify() {
      mixpanel.identify(this.$auth.user.sso_id)
      mixpanel.add_group(
        'company',
        this.credentials.length
          ? this.credentials[0].name
          : 'No Company Tracked'
      )
      mixpanel
        .get_group(
          'company',
          this.credentials.length
            ? this.credentials[0].name
            : 'No Company Tracked'
        )
        .set({
          name: this.credentials.length
            ? this.credentials[0].name
            : 'No Company Tracked',
          id: this.credentials.length
            ? this.credentials[0].id
            : 'No Company Tracked',
        })

      mixpanel.set_group(
        'company',
        this.credentials.length
          ? this.credentials[0].name
          : 'No Company Tracked'
      )

      mixpanel.alias(this.$auth.user.email)
      mixpanel.people.set({
        id: this.$auth.user.id,
        $role: this.$auth.user.role,
        $email: this.$auth.user.email,
        sso_id: this.$auth.user.sso_id,
        $name: this.$auth.user.full_name,
        organization_id: this.$auth.user.organization_id,
      })
    },
    getCompanySize() {
      this.$store.dispatch('organization/getCompanySize')
    },
    getSSOClientToken() {
      this.$axios
        .get(`${process.env.BASE_URL}/web/api/metric/token`, {})
        .then((res) => {
          this.$store.commit(
            'layouts/UPDATE_SSO_CLIENT_TOKEN',
            res.data.access_token
          )
        })
    },
    async processRefreshToken(isUsingSSO) {
      await this.configPromise.catch(() => {})
      const enableSeamlessAuth = this.appConfig?.seamless_auth || false
      if (isUsingSSO) {
        let currentTimestampsRefresh = localStorage.getItem('time_to_refresh')
        let isRefreshing = false
        const intervalToRefresh = setInterval(async () => {
          if (
            new Date().getTime() >= currentTimestampsRefresh &&
            !isRefreshing
          ) {
            const cookieObj = new URLSearchParams(
              document.cookie.replaceAll('&', '%26').replaceAll('; ', '&')
            )
            const validToken = cookieObj.get('qontak._token.hub')
            const validTokenRefresh = cookieObj.get('qontak._refresh_token.hub')
            const chatSsoRefreshToken = cookieObj.get('chat_sso_refresh_token')
            const refreshToken = enableSeamlessAuth
              ? chatSsoRefreshToken
              : validTokenRefresh

            const payload = {
              refresh_token: refreshToken,
              grant_type: 'refresh_token',
            }
            const endpoint =
              `${process.env.HUB_SERVICE_URL}/sso_oauth/token`.replace(
                /([^:]\/)\/+/g,
                '$1'
              )
            isRefreshing = true
            await this.$axios
              .post(endpoint, payload, {
                authorization: validToken,
              })
              .then((resp) => {
                const token = 'bearer ' + resp.data.data.access_token
                const mapRefreshToken = resp.data.data.refresh_token
                const freshToken = resp.data.data.access_token
                const expiredAtTimestamp =
                  (resp.data.data.created_at + resp.data.data.expires_in) * 1000
                const maxAgeInSeconds = Math.floor(
                  (expiredAtTimestamp - Date.now()) / 1000
                )
                const expiredAtTimeStampOneMonth =
                  resp.data.data.created_at + 2592000 // 30 days in seconds
                const maxAgeInSecondsOneMonth = Math.floor(
                  (expiredAtTimeStampOneMonth * 1000 - Date.now()) / 1000
                )

                this.$auth.setToken('hub', token)
                this.$auth.setRefreshToken('hub', mapRefreshToken)

                // Set token to cookies with maxAge
                setStorage('qontak._token.hub', token, {
                  maxAge: maxAgeInSeconds,
                })
                // Set refresh token to cookies with maxAge
                setStorage('qontak._refresh_token.hub', mapRefreshToken, {
                  maxAge: maxAgeInSecondsOneMonth,
                })

                this.$store.commit(
                  'preferences/SET_SSO_REFRESH_TIME',
                  (resp.data.data.created_at +
                    (resp.data.data.expires_in - 600)) *
                    1000
                ) // seconds
                this.$store.commit('preferences/SET_NEW_TOKEN', freshToken) // seconds
                setStorage('qontak._token.hub.share', token, {
                  domain: process.env.HOST_URL,
                  maxAge: 3600,
                })
                this.updateMqttCredWhenRefreshToken()
                const expiredAtDate = generateExpiredAtDate(resp.data.data)

                setSsoSession({
                  token,
                  refreshToken: mapRefreshToken,
                  expiredAt: expiredAtDate,
                  maxAgeInSeconds,
                  maxAgeInSecondsOneMonth,
                  crmTokenAttr: resp.data.data?.crm_token_attr,
                })
                isRefreshing = false
              })
              .catch(() => {
                isRefreshing = false
              })
            clearInterval(intervalToRefresh)
          } else {
            currentTimestampsRefresh = localStorage.getItem('time_to_refresh')
          }
        }, 1000)
      }
    },
    intervalTimeToRefreshToken() {
      this.processRefreshToken(true)
    },
    setRUMUser() {
      datadogRum.setUser({
        id: this.$auth.user.id,
        role: this.$auth.user.role,
        email: this.$auth.user.email,
        sso_id: this.$auth.user.sso_id,
        name: this.$auth.user.full_name,
        organization_id: this.$auth.user.organization_id,
      })
    },
    integrateLouAssist() {
      this.initLouAssistSDK()
      setTimeout(() => {
        this.identifyUserByLouAssist()
      }, 2000)
    },
    initLouAssistSDK() {
      try {
        let sdk = null
        const louSDK = document.getElementsByTagName('script')[0]
        if (document.getElementById('lou-assist')) {
          return
        }
        sdk = document.createElement('script')
        sdk.id = 'lou-assist'
        sdk.src = `https://run.louassist.com/v2.5.1-m?id=${process.env.LOU_ASSIST_TOKEN}`
        louSDK.parentNode.insertBefore(sdk, louSDK)
      } catch (error) {
        return null
      }
    },
    identifyUserByLouAssist() {
      const ssoId = this.$auth.user.sso_id
      const distributedData = {
        qc_user_id: this.$auth.user.id,
        qc_role: this.$auth.user.role,
        qc_company_id: this.credentials.length
          ? this.credentials[0].company_id
          : 'No Company Tracked',
        sso_company_id: process.env.SSO_CLIENT_ID,
        qc_company_size: this.getOrganizationForMetric()['Company Size'],
        qc_company_name: this.getOrganizationForMetric()['Company Name'],
        qc_company_status: null,
        qc_company_go_live_status: null,
      }
      if (window.LOU) window.LOU.identify(ssoId, distributedData)
    },
    async initJimoSDK() {
      if (window.jimo === null || window.jimo === undefined) {
        // Initialize Jimo SDK
        window.jimo = []
        const s = document.createElement('script')
        s.id = 'jimo-sdk'
        s.type = 'text/javascript'
        s.async = true
        s.src = 'https://undercity.usejimo.com/jimo-invader.js'
        window.JIMO_PROJECT_ID = process.env.JIMO_PROJECT_ID
        window.JIMO_MANUAL_INIT = true

        // Wait for script to load before proceeding
        await new Promise((resolve, reject) => {
          s.onload = resolve
          s.onerror = reject
          document.getElementsByTagName('head')[0].appendChild(s)
        })
      } else {
        // Clear localstorage data related to Jimo
        window.jimo?.push(['do', 'storage:reset'])
      }

      await this.$nextTick()

      const eligibleUserPackages = [
        'MIGR-QService - Business Operational',
        'MIGR-QService - Customer Interaction',
        'MIGR-QSales - Business Operational',
        'MIGR-QSales - Customer Interaction',
        'MIGR-Q360 - Business Operational',
        'MIGR-Q360 - Customer Interaction',
        'Qontak 360 - Business Operational',
        'Qontak 360 - Customer Interaction',
        'Qontak Broadcast',
        'Sales Suite Plus - Business Operational',
        'Sales Suite Plus - Customer Interaction',
        'Sales Suite Ultimate - Business Operational',
        'Sales Suite Ultimate - Customer Interaction',
        'Service Suite Plus - Business Operational',
        'Service Suite Plus - Customer Interaction',
        'Service Suite Ultimate - Business Operational',
        'Service Suite Ultimate - Customer Interaction',

        // Separator for new updated packages ============================================

        // "-", // Not needed
        'Gigachat',
        // "MIGR-Q360 - Customer Interaction", // Duplicate - already defined above
        // "MIGR-QSales - Customer Interaction", // Duplicate - already defined above
        // "MIGR-QService - Customer Interaction", // Duplicate - already defined above
        'Omnichannel Basic Enterprise Package',
        'Omnichannel Basic Enterprise Package - Unlimited FAQ & MUV',
        'Omnichannel Business Enterprise Package',
        'Omnichannel Business Enterprise Package - Unlimited FAQ & MUV',
        'Omnichannel Business Package',
        'Omnichannel Business Package - Unlimited FAQ & MUV',
        'Omnichannel Pro Enterprise Package',
        'Omnichannel Pro Enterprise Package - Unlimited FAQ & MUV',
        'Omnichannel Pro Package',
        'Omnichannel Pro Package - Unlimited FAQ & MUV',
        'Omnichannel Starter Enterprise Package',
        'Omnichannel Starter Package',
        // "Qontak 360 - Customer Interaction", // Duplicate - already defined above
        // "Qontak Broadcast", // Duplicate - already defined above
        'Qontak Lite',
        'Qontak Service Suites Enterprise',
        'Qontak Service Suites Enterprise - Omnichannel',
        'Qontak Service Suites Enterprise (Trial)',
        'Qontak Service Suites Plus',
        'Qontak Service Suites Plus - Omnichannel',
        'Qontak Service Suites Plus (Trial)',
        'Qontak Service Suites Ultimate',
        'Qontak Service Suites Ultimate - Omnichannel',
        'Qontak Service Suites Ultimate (Trial)',
        // "Sales Suite Plus - Customer Interaction", // Duplicate - already defined above
        // "Sales Suite Ultimate - Customer Interaction", // Duplicate - already defined above
        // "Service Suite Plus - Customer Interaction", // Duplicate - already defined above
        // "Service Suite Ultimate - Customer Interaction", // Duplicate - already defined above
        'Superchat',
        'Whatsapp Direct API',
        'Whatsapp HUB Notification Panel',
      ]
      const userEligible = eligibleUserPackages.includes(
        this.billing_info?.package_name
      )

      try {
        if (this.$auth.user && this.$auth.user.id && userEligible) {
          window.jimo.push([
            'do',
            'identify',
            [
              this.$auth.user.id || 'unknown_user',
              () => {
                window.jimo.push([
                  'set',
                  'user:name',
                  [this.$auth.user.full_name || 'unknown_user'],
                ])
                window.jimo.push([
                  'set',
                  'user:email',
                  [this.$auth.user.email || 'unknown_user'],
                ])
                window.jimo.push([
                  'set',
                  'user:attributes',
                  [
                    {
                      active_package:
                        this.billing_info?.package_name || 'unknown_package',
                      status: this.billing_info?.status || 'unknown_status',
                      role: this.$auth.user.role || 'unknown_role',
                      company_id:
                        this.$auth.user.company_id || 'unknown_company_id',
                      company_name:
                        this.credentials[0]?.name || 'unknown_company_name',
                    },
                  ],
                ])
              },
            ],
          ])

          // Ensure jimoInit is available before calling it
          if (typeof window.jimoInit === 'function') {
            window.jimoInit()
          } else {
            // eslint-disable-next-line no-console
            console.warn('jimoInit function not available')
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing Jimo user:', error)
      }
    },
    async integrateMoengage(credentials) {
      await moengage.initialize({
        app_id: process.env.MOENGAGE_APP_ID,
        debug_logs: process.env.HUB_ENVIRONMENT === 'production' ? 0 : 0, // clear debug log to 0 again after develop
        enableSPA: true,
      })
      const uniqueId = this.$auth.user.email + ';' + credentials.company_id

      await moengage.add_unique_user_id(uniqueId)
      await moengage.add_first_name(this.$auth.user.full_name)
      await moengage.add_email(this.$auth.user.email)
    },
    getListOfUserDivision() {
      this.$store.dispatch('division/getListOfUserDivision')
    },
    getKBInfo() {
      this.$axios
        .get(`${KB_BASE_URL}/info`, {})
        .then((res) => {
          this.$store.commit('layouts/UPDATE_KB_INFO', res.data.data)
        })
        .catch(() => {
          this.$store.commit('layouts/UPDATE_KB_INFO', {
            kb_client_url: null,
            kb_token: null,
            kb_active: false,
          })
        })
    },
    async getChatbotSubscription() {
      try {
        await chatGPT.getSubscription().then(({ data }) => {
          const features = data?.data?.features
          const askAireneFeatures = features?.find(
            (item) => item.code === 'CP-QONTAKCHAT-2024-0026'
          )
          const scorecardChatbotSubs = features?.find(
            (item) =>
              item.code === 'CP-QONTAKCHAT-2024-0034' ||
              item.code === 'QONTAKCHAT-QA-Manual'
          )
          const aiSummarizationEnabled = features?.find(
            (item) => item.code === 'QONTAKCHAT-AI-Summarization'
          )
          const aiParaphraseEnabled = features?.find(
            (item) => item.code === 'airene_paraphrase'
          )
          this.$store.commit('preferences/SET_CHATBOT_SUBSCRIPTION', {
            list: features ?? [],
            airene_resource_enabled: askAireneFeatures?.enabled,
            scorecard_chatbot_subscription: scorecardChatbotSubs?.enabled,
            ai_summarization_enabled: aiSummarizationEnabled?.enabled,
            ai_paraphrase_enabled: aiParaphraseEnabled?.enabled,
          })
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    checkExpiredBillingEcommerce() {
      this.$store.dispatch(
        'package_usage/billings/checkExpiredEcommerceBilling'
      )
    },
    checkQuotaEcommerce() {
      this.$store.dispatch('package_usage/billings/checkQuotaStoreEcommerce')
    },
    async checkFlaggingScorecardInbox() {
      try {
        const { data } = await chatGPT.getPreferencesScorecardInbox()
        if (data.data?.length > 0) {
          this.$store.commit('organization/UPDATE_PERMISSIONS', {
            name: 'chatbot_scorecard_inbox',
            value: data.data[0].enabled,
          })
        } else {
          this.$store.commit('organization/UPDATE_PERMISSIONS', {
            name: 'chatbot_scorecard_inbox',
            value: true,
          })
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    async checkFlaggingQualityScoreReport() {
      try {
        const { data } = await chatGPT.getGptPreferences({
          group_code: 'report',
          code: 'sqs',
        })
        if (data.data?.length > 0) {
          this.$store.commit('organization/UPDATE_PERMISSIONS', {
            name: 'quality_scoring_report',
            value: data.data[0].enabled,
          })
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    async setChatbotRollout() {
      try {
        await mekariAirene
          .getAireneInformation({
            group_code: 'omnichannel_airene',
            code: 'active_packages',
            enabled: true,
          })
          .then(({ data }) => {
            if (data?.data && data?.data?.length > 0) {
              const billingInfo =
                this.$store.getters['preferences/getBillingInfo']
              const checker = data.data[0].value.packages.includes(
                billingInfo.package_name
              )
              EventBus.$emit('update-chatbot-rollout-package', checker)
              this.$store.commit('organization/UPDATE_PERMISSIONS', {
                name: 'chatbot_rollout_package',
                value: checker ?? false,
              })
            }
          })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    updateMqttCredWhenRefreshToken() {
      this.$store.dispatch('mqtt/getMqttCredentials', { isRefreshToken: true })
    },
    async getChatbotPreferences() {
      try {
        await mekariAirene.getAireneInformation().then(({ data }) => {
          const preferences = data?.data
          const sqsReport = preferences?.find(
            (item) => item?.code === 'sqs' && item?.group_code === 'rollout'
          )
          this.$store.commit('preferences/SET_CHATBOT_PREFERENCES', {
            list: preferences ?? [],
            global_sqs_report: sqsReport?.enabled ?? false,
          })
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    publishBufferedEventsMqttAck() {
      if (this.mqttAck.buffer.length === 0) {
        return
      }

      clogMqtt(
        `Publishing ${this.mqttAck.buffer.length} buffered events`,
        null,
        this.enableLoggingMqtt
      )

      // Clear timer before publishing
      this.clearPublishTimerMqttAck()

      // Copy and clear buffer immediately to prevent duplicate publishes
      const eventsToPublish = [...this.mqttAck.buffer]
      this.mqttAck.buffer = []

      const payload = JSON.stringify(eventsToPublish)
      const topicAck = `notifications/ack/${this.$auth.user.id}`

      this.publishToMqtt(topicAck, payload)
    },
    /**
     * Unified publish method for both SharedWorker and regular MQTT client
     * @param {string} topic - The topic to publish to
     * @param {string} payload - The JSON stringified payload
     */
    publishToMqtt(topic, payload) {
      const isUsingSharedWorker = this.permissions?.mqtt_multitab_session

      if (isUsingSharedWorker) {
        this.publishViaSharedWorker(topic, payload)
      } else {
        this.publishViaRegularMqtt(topic, payload)
      }
    },
    /**
     * Publish via SharedWorker MQTT client
     */
    publishViaSharedWorker(topic, payload) {
      const { getMqttSharedWorkerClient } = require('~/utils/mqttSharedWorker')
      const sharedWorkerClient = getMqttSharedWorkerClient()

      if (!sharedWorkerClient) {
        console.error('SharedWorker MQTT client not available')
        return
      }

      sharedWorkerClient.publish(
        topic,
        payload,
        { qos: 1 },
        (success, error) => {
          if (error) {
            console.error('SharedWorker MQTT publish error:', error)
          } else if (success) {
            clogMqtt(
              `🟢🟢🟢 PUBLISHED EVENTS (SharedWorker): `,
              payload,
              this.enableLoggingMqtt
            )
          }
        }
      )
    },
    /**
     * Publish via regular MQTT client
     */
    publishViaRegularMqtt(topic, payload) {
      const mqttClient = getMqttClient()

      if (!mqttClient) {
        console.error('MQTT client not available')
        return
      }

      mqttClient.publish(topic, payload, { qos: 1 }, (err) => {
        if (err) {
          console.error('MQTT publish error:', err)
        } else {
          clogMqtt(`🟢🟢🟢 PUBLISHED EVENTS: `, payload, this.enableLoggingMqtt)
        }
      })
    },
    /**
     * Check buffer size and determine whether to publish immediately or start timer
     * - If buffer size >= batch limit: publish immediately
     * - If buffer size < batch limit: wait for interval then publish
     */
    checkAndSendBufferedMessagesMqttAck() {
      const flushConfig = this.permissions?.mqtt_log?.flush_config || {}
      const maxBatch = flushConfig.batch || 10

      if (this.mqttAck.buffer.length >= maxBatch) {
        // Buffer limit reached, publish immediately
        clogMqtt(
          `Buffer limit (${maxBatch}) reached. Publishing immediately.`,
          null,
          this.enableLoggingMqtt
        )
        this.publishBufferedEventsMqttAck()
      } else if (this.mqttAck.buffer.length > 0 && !this.mqttAck.publishTimer) {
        // Buffer has messages but below limit, start interval timer
        clogMqtt(
          `Buffer has ${this.mqttAck.buffer.length} messages. Starting interval timer.`,
          null,
          this.enableLoggingMqtt
        )
        this.startPublishTimerMqttAck()
      }
    },
    /**
     * Start interval timer to publish buffered events
     * Timer will publish when interval is exceeded, even if batch limit not reached
     */
    startPublishTimerMqttAck() {
      // Clear existing timer first to prevent multiple timers
      this.clearPublishTimerMqttAck()

      const flushConfig = this.permissions?.mqtt_log?.flush_config || {}
      const publishInterval = flushConfig.interval || 3000

      this.mqttAck.publishTimer = setTimeout(() => {
        if (this.mqttAck.buffer.length > 0) {
          clogMqtt(
            `Interval (${publishInterval}ms) reached. Publishing buffered events.`,
            null,
            this.enableLoggingMqtt
          )
          this.publishBufferedEventsMqttAck()
        }
        this.mqttAck.publishTimer = null
      }, publishInterval)
    },
    /**
     * Clear the publish timer if active
     */
    clearPublishTimerMqttAck() {
      if (this.mqttAck.publishTimer) {
        clearTimeout(this.mqttAck.publishTimer)
        this.mqttAck.publishTimer = null
      }
    },
    getDeviceId() {
      const key = 'device_id'
      let deviceId = localStorage.getItem(key)

      if (!deviceId) {
        deviceId = crypto.randomUUID()
        localStorage.setItem(key, deviceId)
      }

      return deviceId
    },
    setupQontakOneMixpanel() {
      try {
        if (this.$mixpanelQontakOne) {
          if (typeof this.$mixpanelQontakOne.identify === 'function') {
            this.$mixpanelQontakOne.identify(
              this.$auth.user.organization_id || 'No Company ID provided'
            )
          }
          if (typeof this.$mixpanelQontakOne.group === 'function') {
            this.$mixpanelQontakOne.group(
              'Company',
              this.$auth.user.organization_id || 'No Company ID provided'
            )
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error setting up Qontak One Mixpanel:', error)
      }
    },
    disconnectMqtt() {
      disconnectMqttClient()
    },
    disconnectConnectionMqttSharedWorker() {
      disconnectMqttSharedWorker()
    },
  },
}
</script>
